import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: number
  message: string
  type: ToastType
}

interface ToastContextType {
  toast: {
    success: (message: string) => void
    error: (message: string) => void
    warning: (message: string) => void
    info: (message: string) => void
  }
}

const ToastContext = createContext<ToastContextType | null>(null)

let nextId = 0

const config = {
  success: { bg: 'bg-green-50 border-green-400', text: 'text-green-800', icon: CheckCircleIcon, iconColor: 'text-green-400' },
  error: { bg: 'bg-red-50 border-red-400', text: 'text-red-800', icon: XCircleIcon, iconColor: 'text-red-400' },
  warning: { bg: 'bg-yellow-50 border-yellow-400', text: 'text-yellow-800', icon: ExclamationTriangleIcon, iconColor: 'text-yellow-400' },
  info: { bg: 'bg-blue-50 border-blue-400', text: 'text-blue-800', icon: InformationCircleIcon, iconColor: 'text-blue-400' },
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((message: string, type: ToastType) => {
    const id = ++nextId
    setToasts((prev) => [...prev, { id, message, type }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 5000)
  }, [])

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const contextValue: ToastContextType = {
    toast: {
      success: (msg) => addToast(msg, 'success'),
      error: (msg) => addToast(msg, 'error'),
      warning: (msg) => addToast(msg, 'warning'),
      info: (msg) => addToast(msg, 'info'),
    },
  }

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 w-96 pointer-events-none">
          {toasts.map((t) => {
            const c = config[t.type]
            const Icon = c.icon
            return (
              <div
                key={t.id}
                className={`pointer-events-auto border-l-4 rounded-md shadow-lg p-4 ${c.bg} animate-slide-in`}
              >
                <div className="flex items-start">
                  <Icon className={`h-5 w-5 ${c.iconColor} flex-shrink-0 mt-0.5`} />
                  <p className={`ml-3 text-sm font-medium ${c.text} flex-1`}>{t.message}</p>
                  <button
                    onClick={() => removeToast(t.id)}
                    className={`ml-3 flex-shrink-0 ${c.text} hover:opacity-70`}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx.toast
}
